import {z} from "zod";

export const CONTENT_CHANNELS = [
  "email",
  "sms",
  "sms_with_landing_page",
  "mms",
  "mms_with_landing_page",
  "whatsapp",
] as const;
export const STATUSES = ["draft", "active", "inactive"] as const;
export const TEMPLATE_TYPES = ["marketing", "transactional", "validation"] as const;
export const SOURCES = ["manual", "cio_studio", "cio_regular"] as const;
export const EMAIL_DOMAIN_TYPES = ["transactional", "account", "validation", "marketing", "email_ping"] as const;

export const UNSUPPORTED_CHANNELS = new Set<string>(["whatsapp"]);

export const CONTENT_CHANNEL_LABELS: Record<(typeof CONTENT_CHANNELS)[number], string> = {
  email: "Email",
  sms: "SMS",
  sms_with_landing_page: "SMS w/ Landing Page",
  mms: "MMS",
  mms_with_landing_page: "MMS w/ Landing Page",
  whatsapp: "WhatsApp",
};

export const SOURCE_LABELS: Record<(typeof SOURCES)[number], string> = {
  manual: "Manual",
  cio_studio: "Customer.io Studio",
  cio_regular: "Customer.io Campaign",
};

export const EMAIL_DOMAIN_TYPE_LABELS: Record<(typeof EMAIL_DOMAIN_TYPES)[number], string> = {
  transactional: "Transactional",
  account: "Account",
  validation: "Validation",
  marketing: "Marketing",
  email_ping: "Email ping",
};

export const STATUS_LABELS: Record<(typeof STATUSES)[number], string> = {
  draft: "Draft",
  active: "Active",
  inactive: "Inactive",
};

export const TEMPLATE_TYPE_LABELS: Record<(typeof TEMPLATE_TYPES)[number], string> = {
  marketing: "Marketing",
  transactional: "Transactional",
  validation: "Validation",
};

export const NEEDS_BODY = new Set<string>(["email"]);
export const MESSAGING_CHANNELS = new Set<string>([
  "sms",
  "sms_with_landing_page",
  "mms",
  "mms_with_landing_page",
  "whatsapp",
]);
export const MMS_CHANNELS = new Set<string>(["mms", "mms_with_landing_page"]);
export const NEEDS_IMAGE_HTML = MMS_CHANNELS;
export const BODY_FIELD_CHANNELS = new Set<string>(["email", "mms", "mms_with_landing_page"]);
export const SHOWS_CHAR_COUNTER = new Set<string>(["sms", "sms_with_landing_page", "mms", "mms_with_landing_page"]);
export const NEEDS_LANDING_PAGE_SOURCE = new Set<string>(["sms_with_landing_page", "mms_with_landing_page"]);
export const SMS_SEGMENT_LEN = 160;

export const DEFAULT_ES_CHANNEL_MESSAGE_CODE = (import.meta.env.VITE_REACH_API_URL ?? "").includes("staging")
  ? "2"
  : "71";

const LINK_TOKEN = "{{link}}";
const LOGIN_URL_TOKEN = "{{login_url}}";

const requiresBody = (v: Pick<FormValues, "content_channel" | "source">) =>
  NEEDS_BODY.has(v.content_channel) && v.source === "manual";
const requiresSourceLink = (v: Pick<FormValues, "source">) => v.source !== "manual";
const requiresLandingPageSourceLink = (v: Pick<FormValues, "content_channel" | "landing_page_source">) =>
  v.content_channel === "mms_with_landing_page" && v.landing_page_source !== null && v.landing_page_source !== "manual";

export const formSchema = z
  .object({
    name: z.string().min(1, "Required"),
    template_key: z.string().min(1, "Required"),
    segment_ids: z.array(z.number()),
    status: z.enum(STATUSES),
    type: z.enum(TEMPLATE_TYPES),
    content_channel: z.enum(CONTENT_CHANNELS),
    source: z.enum(SOURCES),
    source_link: z.string(),
    pre_header: z.string(),
    channel_message_code: z.string().nullable(),
    weight: z.number().int().min(0).max(100),
    subject: z.string(),
    body: z.string(),
    landing_page_source: z.enum(SOURCES).nullable(),
    landing_page_source_link: z.string(),
    landing_page_body: z.string(),
    email_domain_type: z.enum(EMAIL_DOMAIN_TYPES).nullable(),
  })
  .superRefine((v, ctx) => {
    if (requiresBody(v) && !v.body) {
      ctx.addIssue({code: "custom", path: ["body"], message: "Required"});
    }
    if (requiresSourceLink(v) && !v.source_link) {
      ctx.addIssue({code: "custom", path: ["source_link"], message: "Required"});
    }
    if (requiresLandingPageSourceLink(v) && !v.landing_page_source_link) {
      ctx.addIssue({code: "custom", path: ["landing_page_source_link"], message: "Required"});
    }
    if (v.subject.includes(LINK_TOKEN) && !NEEDS_LANDING_PAGE_SOURCE.has(v.content_channel)) {
      ctx.addIssue({
        code: "custom",
        path: ["subject"],
        message: `${LINK_TOKEN} requires the "${CONTENT_CHANNEL_LABELS.sms_with_landing_page}" or "${CONTENT_CHANNEL_LABELS.mms_with_landing_page}" channel`,
      });
    }
    if (v.subject.includes(LOGIN_URL_TOKEN) && v.content_channel !== "sms" && v.content_channel !== "mms") {
      ctx.addIssue({
        code: "custom",
        path: ["subject"],
        message: `${LOGIN_URL_TOKEN} requires the "${CONTENT_CHANNEL_LABELS.sms}" or "${CONTENT_CHANNEL_LABELS.mms}" channel`,
      });
    }
  });

export type FormValues = z.infer<typeof formSchema>;
